import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiCall } from '../../utils/fetchAPI';

/**
 * The signed-in professional's listing, in the few fields the chrome needs:
 * where it stands, and what kind of business it is.
 *
 * Two things outside any page read it — the banner across the top, and the
 * professional half of the avatar menu — so it is fetched once here rather
 * than by each of them.
 *
 * What the last check concluded is remembered for the tab. The banner sits
 * above everything, so deciding whether to show it costs two round trips —
 * the session, then the listing — and for a professional with an unfinished
 * listing it appeared a beat after the page had painted and pushed the whole
 * thing down: 78px on a desktop, 179px on a phone, measured. Since the answer
 * is the same on every page of a session, the previous one is a sound starting
 * point: the banner is drawn on the first frame and the requests merely
 * confirm it.
 *
 * Session storage, not local storage: the answer must not outlive the tab. It
 * is cleared the moment the account turns out not to be a professional's,
 * which is also what a sign-out looks like from here.
 */
// Versioned: what is cached used to be the banner's own state object, and a
// tab open across the change would read it as a listing.
const CACHE_KEY = 'vie-de-quartier:listing-summary:v3';

export function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);

    return null === raw ? undefined : JSON.parse(raw);
  } catch {
    // Private windows and blocked site data: behave as if nothing was known.
    return undefined;
  }
}

export function writeCache(value) {
  try {
    if (null === value) {
      sessionStorage.removeItem(CACHE_KEY);
      return;
    }

    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Not being able to remember is not worth an error.
  }
}

/**
 * Only what the chrome reads, so a cached copy stays small and current.
 *
 * A professional with no listing at all is not "nothing": it is the state the
 * banner exists for. `null` from this hook means "not a professional", and
 * only that.
 */
export function toSummary(listing) {
  if (!listing) {
    return { status: null, kind: 'SERVICE', ready_to_submit: false, missing_to_submit: [] };
  }

  return {
    status: listing.status,
    kind: listing.kind || 'SERVICE',
    ready_to_submit: listing.ready_to_submit,
    missing_to_submit: listing.missing_to_submit,
  };
}

export function useMyListingSummary() {
  const { isProvider, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const [summary, setSummary] = useState(() => readCache() ?? null);

  useEffect(() => {
    let cancelled = false;

    // While the session is still being fetched, nothing is known yet — and
    // `isProvider` reads false in the meantime. Acting on it here would hide
    // what the cache has just drawn, which is the jump all over again.
    if (authLoading) {
      return undefined;
    }

    if (!isProvider) {
      setSummary(null);
      writeCache(null);
      return undefined;
    }

    const load = async () => {
      try {
        const response = await apiCall('/me/listing', { method: 'GET' });

        if (!response.ok || cancelled) return;

        const data = await response.json();

        if (cancelled) return;

        const next = toSummary(data.data);

        setSummary(next);
        writeCache(next);
      } catch {
        // The chrome is not worth an error message of its own.
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // Re-checked on navigation, so finishing the wizard or changing the kind
    // of listing shows up without a reload.
  }, [isProvider, authLoading, pathname]);

  return summary;
}
